import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, ChevronLeft, ShoppingCart, Search, Trash2, Check,
  // Category icon set — kept broad + neutral so we can pattern-match
  // arbitrary category names without shipping bespoke SVGs per client.
  Cookie, Droplets, Snowflake, IceCreamCone, Coffee, Wheat, Milk, Utensils,
  ShoppingBasket, Cake, CandyCane, Sandwich, Beef, Fish, Beer, Egg, Package,
} from 'lucide-react';
import BranchShell from '../components/BranchShell';
import CutoffBlockPage from '../components/CutoffBlockPage';
import { resolveImageUrl } from '../components/ImageUpload';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useBranchAuth, API } from '../context/BranchAuthContext';
import useBranchColumnHidden from '../hooks/useBranchColumnHidden';
import { useToast } from '../hooks/use-toast';

/**
 * Pick a lucide icon for a category by matching keywords against its
 * name / slug. Falls back to a generic Package icon so no category is
 * ever iconless. This keeps the picker admin-free: as soon as a
 * category is named "Ice Cream" or "Frozen Foods" it gets the right icon.
 */
const CATEGORY_ICON_RULES = [
  [/(ice ?cream|gelato|kulfi)/, IceCreamCone],
  [/(frozen|freezer|deep ?freeze)/, Snowflake],
  [/(water|mineral|bottle)/, Droplets],
  [/(chips|crisp|snack|namkeen|nachos)/, Cookie],
  [/(chocolate|candy|sweet|toffee)/, CandyCane],
  [/(cake|bakery|bread|bun|biscuit|cookie|wafer)/, Cake],
  [/(coffee|tea|cappucc|espresso)/, Coffee],
  [/(juice|beverage|drink|soda|soft ?drink|energy)/, Beer],
  [/(milk|dairy|yogurt|cheese|cream)/, Milk],
  [/(egg)/, Egg],
  [/(meat|beef|lamb|chicken|poultry|mutton)/, Beef],
  [/(fish|seafood|prawn|shrimp)/, Fish],
  [/(rice|grain|flour|wheat|noodle|pasta)/, Wheat],
  [/(spice|sauce|condiment|masala|oil)/, Utensils],
  [/(sandwich|burger|pizza|ready ?meal)/, Sandwich],
  [/(grocery|essentials|staples|kitchen|pantry)/, ShoppingBasket],
];

const iconForCategory = (cat) => {
  const s = `${cat?.name || ''} ${cat?.slug || ''}`.toLowerCase();
  for (const [re, Icon] of CATEGORY_ICON_RULES) if (re.test(s)) return Icon;
  return Package;
};

const BranchNewOrder = () => {
  const { token, branch, authHeaders, isSuperBypass } = useBranchAuth();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  // Live stock map — decoupled from the `products` array so the 15-min
  // background refresh can update ONLY the "Available qty" cell without
  // triggering a re-render on the rest of the page (search box, cart
  // inputs, active category, etc. all stay pinned).
  // Shape: { [product_id]: { batches: [...], stock_qty: number } }
  const [stockMap, setStockMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null); // {slug, name, color, bg}
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState({}); // { [product_id]: { product, qty } }
  const [notes, setNotes] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCart, setShowCart] = useState(false);
  // Feb 2026 v14 — Order cut-off check. When present, the order form
  // is replaced with a countdown block-page.
  const [cutoff, setCutoff] = useState(null);
  // Cached logo URL for the countdown page (fetched alongside the
  // cutoff status so we only pull site_settings once).
  const [siteLogo, setSiteLogo] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => { document.title = 'Branch Portal — New Order'; }, []);

  // Feb 2026 v14 — fetch the current cutoff state on mount + poll
  // every 30s so the block page auto-lifts the moment the window ends
  // (and vice-versa for windows that start while a branch is browsing).
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const [c, s] = await Promise.all([
          axios.get(`${API}/branch/cutoff/current`, { headers: authHeaders }),
          axios.get(`${API}/settings`).catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setCutoff(c.data && c.data.active ? c.data : null);
        setSiteLogo(resolveImageUrl(s.data?.logo_image || ''));
      } catch { /* non-fatal — form still works */ }
    };
    tick();
    const id = setInterval(tick, 30 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [token, authHeaders]);

  // Pre-select the first customer_number on the account (branches with
  // only one number never see the picker; multi-number branches can
  // change it inside the cart drawer).
  useEffect(() => {
    if (!customerNumber && branch?.customer_numbers?.length) {
      setCustomerNumber(branch.customer_numbers[0]);
    }
  }, [branch, customerNumber]);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          // /branch/categories is decoupled from storefront `is_visible`
          // — storefront category visibility has ZERO effect on branches
          // (and vice-versa). Only per-branch allowed_category_slugs +
          // super-admin bypass drive what comes back.
          axios.get(`${API}/branch/categories`, { headers: authHeaders }),
          axios.get(`${API}/products?limit=1000`),
        ]);
        setCategories(cRes.data || []);
        setProducts(pRes.data || []);
        // Seed the stock map from the initial products fetch.
        // Feb 2026 (Step 1) — carry the placement-derived fields
        // instead of the retired ledger `stock_qty` / `batches[]`
        // fallback stack (availability now depends solely on
        // placement-derived `on_hand_qty` / `placements_sum_qty`).
        const seed = {};
        (pRes.data || []).forEach((p) => {
          seed[p.id] = {
            on_hand_qty: p.on_hand_qty || 0,
            placements_sum_qty: p.placements_sum_qty || 0,
            allocated_qty: p.allocated_qty || 0,
          };
        });
        setStockMap(seed);
      } catch (err) {
        toast({ title: 'Failed to load catalog', description: err?.response?.data?.detail || err?.message });
      } finally { setLoading(false); }
    };
    setLoading(true);
    load();
    // Silent Available-qty refresh (Feb 2026): every 15 min, fetch the
    // products list but only splice the stock fields into `stockMap`.
    // The `products` state is never re-assigned, so nothing else on the
    // page re-renders: the search box keeps focus, the cart qty inputs
    // keep their values, and the active category stays put. Only the
    // <Available qty> <td> DOM nodes flip their number.
    const poll = setInterval(() => {
      axios.get(`${API}/products?limit=1000`)
        .then(({ data }) => {
          const next = {};
          (data || []).forEach((p) => {
            next[p.id] = {
              // Feb 2026 (Step 1) — carry the placement-derived
              // fields into `stockMap` too so the 15-min silent
              // refresh actually refreshes availability. Previously
              // only ledger `stock_qty` + `batches` were spliced in,
              // which meant `availableQty()` fell back to the
              // page-load `on_hand_qty` after every refresh and any
              // in-hand movement in the last 15 minutes never
              // surfaced on the branch cart.
              on_hand_qty: p.on_hand_qty || 0,
              placements_sum_qty: p.placements_sum_qty || 0,
              allocated_qty: p.allocated_qty || 0,
            };
          });
          setStockMap(next);
        })
        .catch(() => {});
    }, 15 * 60 * 1000); // 15 minutes
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const visibleCategories = useMemo(
    () => {
      // Categories already come pre-scoped from /branch/categories —
      // decoupled from storefront `is_visible`. Just sort them by
      // display order for the branch's own landing page.
      return (categories || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    [categories],
  );

  const filteredProducts = useMemo(() => {
    let list = products || [];
    if (activeCategory) list = list.filter((p) => p.category_slug === activeCategory.slug);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q),
    );
    return list;
  }, [products, activeCategory, search]);

  const setQty = (prod, qty) => {
    setCart((prev) => {
      const next = { ...prev };
      const n = Math.max(0, Math.floor(Number(qty) || 0));
      if (n <= 0) delete next[prod.id];
      else next[prod.id] = { product: prod, qty: n };
      return next;
    });
  };
  // +/- buttons removed (Feb 2026) — branches type qty directly. If we
  // ever need to nudge qty by 1 programmatically, wrap `setQty` inline.

  const cartItems = Object.values(cart);
  const cartCount = cartItems.reduce((s, it) => s + it.qty, 0);
  // Admin-controlled column visibility — same hook as BranchOrders.
  const { isHidden: isBranchColHidden } = useBranchColumnHidden();

  // Real-time stock validation (Feb 2026).  For every cart line, compute
  // the available qty against the same silently-refreshed `stockMap` used
  // by the product list, then build:
  //   * `availByPid` — quick lookup so <ProductList> and the cart drawer
  //     can show "Exceeds available stock (available: X)" beneath each
  //     over-limit input WITHOUT recomputing on every render.
  //   * `overLimitItems` — the list of offending cart lines used to
  //     block submit + render an error near the submit button.
  const availByPid = useMemo(() => {
    const m = {};
    for (const p of products || []) m[p.id] = availableQty(p, stockMap);
    return m;
  }, [products, stockMap]);
  const overLimitItems = useMemo(
    () => cartItems.filter(({ product, qty }) => qty > (availByPid[product.id] ?? Infinity)),
    [cartItems, availByPid],
  );

  const submit = async () => {
    if (cartItems.length === 0) return;
    if (overLimitItems.length > 0) return;  // guard — button also disables
    setSubmitting(true);
    try {
      const body = {
        items: cartItems.map(({ product, qty }) => ({ product_id: product.id, qty })),
        notes: notes.trim(),
      };
      // Only forward customer_number when the branch has one selected —
      // branches with no linked cnum still place valid orders (backend
      // accepts an empty customer_number for branch accounts).
      if (customerNumber) body.customer_number = customerNumber;
      const { data } = await axios.post(`${API}/branch/orders`, body, { headers: authHeaders });
      toast({ title: `Order ${data.order_number} placed`, description: 'Track its status on My Orders.' });
      setCart({}); setNotes(''); setShowCart(false);
      navigate('/branch/orders');
    } catch (err) {
      toast({ title: 'Submit failed', description: err?.response?.data?.detail || err?.message });
    } finally { setSubmitting(false); }
  };

  if (!token) return <Navigate to="/branch/login" replace />;

  return (
    <BranchShell>
      {/* Feb 2026 v14 — Full-screen cutoff block. Super-admins
          impersonating a branch bypass it (matches backend). */}
      {cutoff && !isSuperBypass && (
        <CutoffBlockPage
          toIso={cutoff.to_ts}
          label={cutoff.label}
          logoUrl={siteLogo}
          onExpired={() => setCutoff(null)}
        />
      )}
      {loading ? (
        <div className="rounded-2xl bg-white border border-neutral-200 p-10 text-center text-sm text-neutral-500 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading catalog…
        </div>
      ) : !activeCategory ? (
        <CategoriesGrid categories={visibleCategories} onPick={setActiveCategory} products={products} />
      ) : (
        <ProductList
          category={activeCategory}
          products={filteredProducts}
          stockMap={stockMap}
          search={search}
          onSearch={setSearch}
          cart={cart}
          onQty={setQty}
          onBack={() => { setActiveCategory(null); setSearch(''); }}
          availByPid={availByPid}
          isBranchColHidden={isBranchColHidden}
        />
      )}

      {/* Cart FAB — visible whenever there's anything in the cart */}
      {cartCount > 0 && !showCart && (
        <button
          type="button"
          onClick={() => setShowCart(true)}
          data-testid="branch-cart-fab"
          className="fixed bottom-5 right-5 z-40 rounded-full bg-[#0F172A] text-white shadow-xl px-5 py-3 inline-flex items-center gap-3 hover:bg-black transition"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="text-sm font-semibold">
            {cartCount} item{cartCount !== 1 ? 's' : ''}
          </span>
          <span className="text-sm font-semibold opacity-80">Review order</span>
        </button>
      )}

      {/* Cart drawer */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowCart(false)}>
          <div
            className="bg-white w-full sm:max-w-lg sm:rounded-2xl max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="branch-cart-drawer"
          >
            <div className="flex items-center justify-between p-4 border-b border-neutral-100 sticky top-0 bg-white z-10">
              <div>
                <div className="text-[10px] font-bold tracking-wider text-neutral-500 uppercase">Review order</div>
                <div className="text-lg font-bold text-[#0F172A]">Your cart</div>
              </div>
              <button type="button" onClick={() => setShowCart(false)} className="text-neutral-500 hover:text-neutral-900 p-2" aria-label="Close cart">
                <ChevronLeft className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {cartItems.length === 0 && (
                <div className="text-sm text-neutral-500 text-center py-6">Your cart is empty.</div>
              )}
              {cartItems.map(({ product, qty }) => {
                const availLine = availByPid?.[product.id];
                const overLine = availLine != null && qty > availLine;
                return (
                <div key={product.id} className="flex flex-col gap-1 pb-3 border-b border-neutral-100 last:border-0" data-testid={`branch-cart-item-${product.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#0F172A] truncate">{product.name}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">
                        {product.uom || 'ctn'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={qty}
                        onChange={(e) => setQty(product, e.target.value)}
                        className={`w-24 h-10 text-center text-base font-semibold border rounded-lg px-2 tabular-nums focus:outline-none ${overLine
                          ? 'border-red-500 bg-red-50 text-red-700 focus:ring-2 focus:ring-red-400'
                          : 'border-neutral-300 focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]'}`}
                        aria-invalid={overLine ? 'true' : 'false'}
                        data-testid={`branch-cart-qty-${product.id}`}
                      />
                    </div>
                    <button type="button" onClick={() => setQty(product, 0)} className="text-neutral-400 hover:text-red-500 p-1" aria-label="Remove">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {overLine && (
                    <div
                      className="text-[11px] font-semibold text-red-600 pl-1"
                      data-testid={`branch-cart-qty-exceeds-${product.id}`}
                    >
                      Exceeds available stock (available: {availLine})
                    </div>
                  )}
                </div>
                );
              })}

              {(branch?.customer_numbers?.length || 0) > 1 && (
                <div className="pt-2">
                  <label htmlFor="cnum-picker" className="block text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1">Bill to customer number</label>
                  <select
                    id="cnum-picker"
                    value={customerNumber}
                    onChange={(e) => setCustomerNumber(e.target.value)}
                    className="w-full text-sm border border-neutral-300 rounded-lg px-3 py-2 bg-white"
                    data-testid="branch-cnum-picker"
                  >
                    {branch.customer_numbers.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="branch-cart-notes" className="block text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-1">Notes (optional)</label>
                <Input
                  id="branch-cart-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Delivery instructions, PO reference, etc."
                  className="text-sm"
                  data-testid="branch-cart-notes"
                />
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-neutral-200">
                <div className="text-sm text-neutral-500">Total items</div>
                <div className="text-xl font-bold text-[#0F172A] tabular-nums" data-testid="branch-cart-total-count">{cartCount}</div>
              </div>

              {overLimitItems.length > 0 && (
                <div
                  className="mt-2 rounded-lg border border-red-200 bg-red-50 text-red-800 text-xs px-3 py-2 leading-snug"
                  data-testid="branch-cart-overlimit-error"
                >
                  <div className="font-bold uppercase tracking-wider text-[10px] text-red-700 mb-1">
                    Cannot place order — quantity exceeds available stock
                  </div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {overLimitItems.map(({ product, qty }) => (
                      <li key={product.id} data-testid={`branch-cart-overlimit-item-${product.id}`}>
                        <span className="font-semibold">{product.name}</span>
                        {' — you entered '}
                        <span className="font-bold">{qty}</span>
                        {', available '}
                        <span className="font-bold">{availByPid[product.id]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <Button
                onClick={submit}
                disabled={submitting || cartItems.length === 0 || overLimitItems.length > 0}
                className="w-full h-11 rounded-xl bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold text-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="branch-cart-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Place order</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </BranchShell>
  );
};

// ============================================================
// Sub-components
// ============================================================
const CategoriesGrid = ({ categories, onPick, products }) => {
  // Cheap per-category product count so branches see catalog depth
  // at a glance before drilling in.
  const countBySlug = useMemo(() => {
    const m = {};
    for (const p of products || []) m[p.category_slug] = (m[p.category_slug] || 0) + 1;
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold text-[#0F172A]">Place a new order</h1>
        <p className="text-sm text-neutral-500 mt-1">Browse categories, pick your products, review + submit.</p>
      </div>
      {categories.length === 0 ? (
        <div className="rounded-2xl bg-white border border-neutral-200 p-10 text-center text-sm text-neutral-500">
          No categories are visible right now.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="branch-categories-grid">
          {categories.map((cat) => {
            const Icon = iconForCategory(cat);
            const bg = cat.bg || '#DEECF9';
            const fg = cat.color || '#0078D4';
            const productCount = countBySlug[cat.slug] || 0;
            return (
              <button
                key={cat.id || cat.slug}
                type="button"
                onClick={() => onPick(cat)}
                data-testid={`branch-category-${cat.slug}`}
                className="group text-left bg-white rounded-2xl border border-neutral-200 p-5 hover:border-[#0078D4] hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-105 transition-transform"
                  style={{ backgroundColor: bg }}
                >
                  <Icon className="w-7 h-7" style={{ color: fg }} strokeWidth={1.6} />
                </div>
                <div className="text-sm font-bold text-[#0F172A] leading-tight">{cat.name}</div>
                <div className="text-[11px] text-neutral-500 mt-1">
                  {productCount} product{productCount !== 1 ? 's' : ''}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Compute live "available" qty for a product — first checks the
// silently-refreshed `stockMap` (updated every 15 min without any
// visible re-render), falling back to the product's own batches +
// legacy stock_qty on the initial paint / for anything not (yet) in
// the map. Feb 2026 — subtracts `allocated_qty` (stock committed to
// other confirmed orders but not yet dispatched) so branches never see
// or order stock that's already locked.
//
// Feb 2026 (user directive — Step 1) — REMOVED the `batches[]` and
// `stock_qty` fallbacks. Branch ordering availability now comes SOLELY
// from the product's actual `warehouse_placements` (surfaced by
// `/api/products` as `on_hand_qty` / `placements_sum_qty`, summed
// across every warehouse) minus `allocated_qty`. A product with no
// pallet quantity in the placements table now shows 0 available —
// no silent borrowing of the ledger snapshot or the stale batch
// totals.
const availableQty = (p, stockMap) => {
  const live = stockMap?.[p?.id];
  const allocated = Number(live?.allocated_qty ?? p?.allocated_qty ?? 0);
  const physical = Number(
    live?.on_hand_qty ?? p?.on_hand_qty ??
    live?.placements_sum_qty ?? p?.placements_sum_qty ?? 0,
  );
  return Math.max(0, physical - allocated);
};

const ProductList = ({ category, products, stockMap, search, onSearch, cart, onQty, onBack, availByPid, isBranchColHidden }) => {
  const Icon = iconForCategory(category);
  // Local no-op fallback when the parent didn't pass the hook accessor
  // (defensive — keeps ProductList render-safe if the prop is dropped).
  const hidden = typeof isBranchColHidden === 'function' ? isBranchColHidden : () => false;
  // Precompute grid columns so removing SKU or In-Hand still gives a
  // balanced desktop layout. Product + Qty are always present.
  const showSku = !hidden('new_order.sku');
  const showInHand = !hidden('new_order.in_hand');
  const showAllocated = !hidden('new_order.allocated');
  const gridCols = `${showSku ? '110px ' : ''}1fr ${showInHand ? '140px ' : ''}${showAllocated ? '120px ' : ''}140px`;
  return (
    <div>
      <div className="mb-4 flex items-center gap-2 sm:gap-3 flex-wrap">
        <button
          type="button"
          onClick={onBack}
          className="text-xs font-semibold text-neutral-600 hover:text-[#0F172A] inline-flex items-center gap-1"
          data-testid="branch-back-to-categories"
        >
          <ChevronLeft className="w-4 h-4" /> All categories
        </button>
        <span className="text-neutral-300 hidden sm:inline">/</span>
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: category.bg || '#DEECF9' }}
          >
            <Icon className="w-4 h-4" style={{ color: category.color || '#0078D4' }} strokeWidth={1.8} />
          </div>
          <h1 className="font-display text-lg sm:text-xl font-bold text-[#0F172A] truncate">{category.name}</h1>
        </div>
        {!hidden('new_order.search') && (
          <div className="w-full sm:w-auto sm:ml-auto sm:max-w-xs">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Search products or SKU"
                className="text-sm pl-9"
                data-testid="branch-product-search"
              />
            </div>
          </div>
        )}
      </div>

      {products.length === 0 ? (
        <div className="rounded-2xl bg-white border border-neutral-200 p-10 text-center text-sm text-neutral-500">
          No matching products in this category.
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden" data-testid="branch-products-table">
          {/* Desktop table header — hidden on mobile in favour of the card layout below */}
          <div
            className="hidden sm:grid gap-3 px-4 py-3 bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider font-semibold"
            style={{ gridTemplateColumns: gridCols }}
          >
            {showSku && <div>SKU</div>}
            <div>Product</div>
            {showInHand && <div className="text-right">In Hand</div>}
            {showAllocated && <div className="text-right">Allocated</div>}
            <div className="text-right">Qty</div>
          </div>
          {products.map((p) => {
            const qty = cart[p.id]?.qty || 0;
            const avail = (availByPid && availByPid[p.id] != null) ? availByPid[p.id] : availableQty(p, stockMap);
            const isOut = avail <= 0;
            // Over-limit = user typed a qty that exceeds current available.
            // The row highlights + shows a red inline error below the input.
            const overLimit = !isOut && qty > avail;
            const availClass = isOut
              ? 'text-red-600'
              : avail < 10
                ? 'text-amber-600'
                : 'text-emerald-700';
            return (
              <div
                key={p.id}
                className={`border-t border-neutral-100 ${qty > 0 ? 'bg-[#F4FAFF]' : ''} ${isOut ? 'opacity-90' : ''}`}
                data-testid={`branch-product-row-${p.id}`}
              >
                {/* Mobile layout — two-line card, qty inline with product name */}
                <div className="sm:hidden flex flex-col gap-1.5 p-3">
                  <div className="flex items-center justify-between">
                    {!hidden('new_order.sku') ? (
                      <div className="font-mono text-[11px] text-neutral-500">{p.sku || '—'}</div>
                    ) : <div />}
                    {!hidden('new_order.in_hand') && (
                      <div
                        className={`text-xs font-semibold tabular-nums ${availClass}`}
                        data-testid={`branch-avail-${p.id}-mobile`}
                      >
                        In Hand: {avail} <span className="text-neutral-400 font-normal">{p.uom || 'ctn'}</span>
                      </div>
                    )}
                    {!hidden('new_order.allocated') && (
                      <div
                        className="text-xs tabular-nums text-neutral-500"
                        data-testid={`branch-allocated-${p.id}-mobile`}
                        title="Qty already allocated to confirmed orders"
                      >
                        Allocated: <span className="font-semibold text-[#0F172A]">{Number(stockMap?.[p.id]?.allocated_qty ?? p.allocated_qty ?? 0)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0 text-sm font-medium text-[#0F172A] leading-snug">
                      {p.name}
                      {!hidden('new_order.size') && p.size && <span className="text-neutral-400 text-xs"> · {p.size}</span>}
                      {!hidden('new_order.oos_badge') && isOut && (
                        <span
                          className="ml-2 inline-block align-middle px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wide"
                          data-testid={`branch-oos-badge-${p.id}`}
                        >
                          Out of Stock
                        </span>
                      )}
                    </div>
                    <input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={qty || ''}
                      onChange={(e) => onQty(p, e.target.value)}
                      placeholder={isOut ? '—' : '0'}
                      disabled={isOut}
                      className={`w-20 h-10 shrink-0 text-center text-base font-semibold border rounded-lg px-2 tabular-nums focus:outline-none ${isOut
                        ? 'bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed'
                        : overLimit
                          ? 'border-red-500 bg-red-50 text-red-700 focus:ring-2 focus:ring-red-400'
                          : 'border-neutral-300 focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]'}`}
                      data-testid={`branch-qty-input-${p.id}`}
                      aria-disabled={isOut ? 'true' : 'false'}
                      aria-invalid={overLimit ? 'true' : 'false'}
                      title={isOut ? 'Out of stock — cannot order this item' : ''}
                    />
                  </div>
                  {overLimit && (
                    <div
                      className="text-[11px] font-semibold text-red-600 mt-0.5"
                      data-testid={`branch-qty-exceeds-${p.id}`}
                    >
                      Exceeds available stock (available: {avail})
                    </div>
                  )}
                </div>

                {/* Desktop layout — 4-column grid */}
                <div
                  className="hidden sm:grid sm:gap-3 sm:px-4 sm:py-3 sm:items-center"
                  style={{ gridTemplateColumns: gridCols }}
                >
                  {showSku && (
                    <div className="font-mono text-xs text-neutral-500">{p.sku || '—'}</div>
                  )}
                  <div className="text-sm font-medium text-[#0F172A] leading-snug flex items-center gap-2 flex-wrap">
                    <span>
                      {p.name}
                      {!hidden('new_order.size') && p.size && <span className="text-neutral-400 text-xs"> · {p.size}</span>}
                    </span>
                    {!hidden('new_order.oos_badge') && isOut && (
                      <span
                        className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wide"
                        data-testid={`branch-oos-badge-desktop-${p.id}`}
                      >
                        Out of Stock
                      </span>
                    )}
                  </div>
                  {showInHand && (
                    <div
                      className={`text-right tabular-nums font-semibold text-sm ${availClass}`}
                      data-testid={`branch-avail-${p.id}`}
                    >
                      {avail} <span className="text-neutral-400 font-normal text-[10px]">{p.uom || 'ctn'}</span>
                    </div>
                  )}
                  {showAllocated && (
                    <div
                      className="text-right tabular-nums text-sm text-neutral-600"
                      data-testid={`branch-allocated-${p.id}`}
                      title="Qty already allocated to confirmed orders"
                    >
                      <span className="font-semibold text-[#0F172A]">
                        {Number(stockMap?.[p.id]?.allocated_qty ?? p.allocated_qty ?? 0)}
                      </span>
                    </div>
                  )}
                  <div className="text-right">
                    <input
                      type="number"
                      min="0"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={qty || ''}
                      onChange={(e) => onQty(p, e.target.value)}
                      placeholder={isOut ? '—' : '0'}
                      disabled={isOut}
                      className={`w-28 h-10 text-center text-sm font-semibold border rounded-lg px-2 tabular-nums focus:outline-none ${isOut
                        ? 'bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed'
                        : overLimit
                          ? 'border-red-500 bg-red-50 text-red-700 focus:ring-2 focus:ring-red-400'
                          : 'border-neutral-300 focus:ring-2 focus:ring-[#0078D4]/40 focus:border-[#0078D4]'}`}
                      data-testid={`branch-qty-input-desktop-${p.id}`}
                      aria-disabled={isOut ? 'true' : 'false'}
                      aria-invalid={overLimit ? 'true' : 'false'}
                      title={isOut ? 'Out of stock — cannot order this item' : ''}
                    />
                    {overLimit && (
                      <div
                        className="text-[11px] font-semibold text-red-600 mt-1 text-right"
                        data-testid={`branch-qty-exceeds-desktop-${p.id}`}
                      >
                        Exceeds available stock (available: {avail})
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BranchNewOrder;
